import { createRef, useEffect, useState, useContext } from 'react';
import { renderWidgets } from '@/components/Froala/widgets/renderWidget';
import { clsx } from 'clsx';
import { fancyWrapper } from '@/util';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { useParams } from 'react-router-dom';

type FroalaTextareaViewProps = {
  value: string | string[];
  correction?: any;
  className?: string;
  setWidgets?: (w: any) => void;
  isCorrected?: boolean;
  page?: number;
  lesson?: any;
  showAnswers?: boolean;
  correctionType?: string;
  courseStatus?: string | undefined;
  setIsLoaded?: (v: boolean) => void;
  isExtraInfo?: boolean;
};

const FroalaTextareaView = ({
  value,
  correction,
  className,
  setWidgets,
  isCorrected,
  page = 0,
  showAnswers = false,
  correctionType = 'automatic',
  courseStatus = undefined,
  setIsLoaded,
  isExtraInfo = false,
}: FroalaTextareaViewProps) => {
  const { auth } = useContext(AuthContext);
  const { studentId } = useParams();
  const isFollowUp =
    window.location.pathname.includes('/follow-up/') &&
    window.location.pathname.includes('/result/');
  const userId: any = isFollowUp
    ? studentId
      ? studentId
      : auth?.user.id
    : auth?.user.id;
  const [widgets, setMyWidgets] = useState<any>({});
  const [loaded, setLoaded] = useState<boolean>(false);

  // keep original ref semantics
  const myRef = createRef<HTMLDivElement>();

  const valueArr = Array.isArray(value) ? value : [value];

  // keep original behavior on page switch
  useEffect(() => {
    setTimeout(() => {
      setMyWidgets(Object.assign({}, widgets));
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // keep original widget logic (mutation + clone)

  useEffect(() => {
    const newWidgets = renderWidgets(
      myRef.current,
      correction,
      {
        isCorrected,
        showAnswers,
        correctionType,
        courseStatus,
      },
      userId,
      isExtraInfo
    ) as any;

    for (const key in newWidgets) {
      if (newWidgets[key]) widgets[key] = newWidgets[key];
    }

    const temp = { ...widgets };
    setMyWidgets(temp);
    setWidgets && setWidgets(temp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, correction]);

  // ✅ reliable, idempotent fancybox wrapper (no loops)
  useEffect(() => {
    const root = myRef.current;
    if (!root) return;

    let destroyed = false;
    let rafId: number | null = null;

    const run = () => {
      if (destroyed) return;
      fancyWrapper(root);
    };

    const schedule = () => {
      if (destroyed) return;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(run);
    };

    // initial run after commit
    schedule();

    // rerun when images finish loading
    const imgs = Array.from(root.querySelectorAll('img'));
    const onImgDone = () => schedule();
    for (const img of imgs) {
      if (!img.complete) {
        img.addEventListener('load', onImgDone, { once: true });
        img.addEventListener('error', onImgDone, { once: true });
      }
    }

    // rerun when DOM mutates (widgets may inject content later)
    const mo = new MutationObserver(() => schedule());
    mo.observe(root, { subtree: true, childList: true, attributes: true });

    // preserve original "probably loaded now" signal
    const loadedId = requestAnimationFrame(() => {
      if (destroyed) return;
      setLoaded(true);
      setIsLoaded && setIsLoaded(false);
    });

    return () => {
      destroyed = true;
      if (rafId) cancelAnimationFrame(rafId);
      cancelAnimationFrame(loadedId);
      mo.disconnect();
    };
    // IMPORTANT: same dependency as before to avoid behavior change
  }, [widgets, setIsLoaded]);

  if (!value) return null;

  return (
    <>
      {/* ref must cover all pages: renderWidgets only scans inside it */}
      <div ref={myRef} style={{ display: 'contents' }}>
        {valueArr.map((value, i) => (
          <div
            key={i}
            style={{ display: page !== i ? 'none' : undefined }}
            className={clsx('fr-view', className)}
            dangerouslySetInnerHTML={{
              __html: value
                ?.replace?.(/ contenteditable="true"/g, '')
                ?.replace?.(/remove_me_gEE8QIPZ6LEay22SdSCU/g, 'hidden'),
            }}
          />
        ))}
      </div>

      {Object.values(widgets).map((x: any) => x.portal)}
      <div className={clsx(loaded && 'page-loaded')} />
    </>
  );
};

export { FroalaTextareaView };
